import { useState, useEffect, useRef, useCallback } from 'react';
import { User } from '../api/model';

export const useCurrentUser = (): User => {
  const [user, setUser] = useState<User>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : {};
  });

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        setUser(e.newValue ? JSON.parse(e.newValue) : {});
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return user;
};

/**
 * 自定义定时器 hook
 * @param callback 定时器回调函数
 * @param delay 定时器延迟时间（毫秒）
 * @param enabled 是否启用定时器
 */
export const useInterval = (callback: () => void, delay: number, enabled: boolean) => {
  const callbackRef = useRef(callback);
  const timerRef = useRef<number | null>(null);
  const delayRef = useRef(delay);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  useEffect(() => {
    delayRef.current = delay;
  }, [delay]);
  useEffect(() => {
    if (enabled) {
      const timer = window.setInterval(() => callbackRef.current(), delayRef.current);
      timerRef.current = timer;
    }
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled]);
};

/**
 * 自定义倒计时 hook
 * @param initialCount 初始倒计时值（秒）
 * @param initialEnabled 是否初始启用倒计时
 * @returns [当前倒计时值, 是否倒计时结束, 重置倒计时函数]
 */
export const useCountdown = (initialCount: number, initialEnabled: boolean): [number, boolean, () => void] => {
  
  const initialCountRef = useRef(initialCount);
  
  if (initialCountRef.current !== initialCount) {
    initialCountRef.current = initialCount;
  }
  
  const [count, setCount] = useState(initialCount);
  const [ended, setEnded] = useState(!initialEnabled);
  
  useInterval(() => {
    setCount(count => {
      const newCount = count - 1;
      return newCount;
    });
    if (count <= 1) {
      setEnded(true);
    }
  }, 1000, !ended);
  
  const reset = useCallback(() => {
    setCount(initialCountRef.current);
    setEnded(false);
  }, []);
  
  return [count, ended, reset];
};